import { FormattedMessage } from 'react-intl';

import { Divider, Grid, Tooltip, Typography } from '@mui/material';

import clsx from 'clsx';
import styled, { css } from 'styled-components';

import { Offering } from '@arrived/models';

import { PropertyAmountBreakdownTitle } from './PropertyAmountBreakdownTitle';
import { PropertyAmountBreakdownValue } from './PropertyAmountBreakdownValue';

import { useIsMobile } from '../../../hooks/useIsMobile';

export interface PropertyAmountBreakdownProps {
  offering: Offering;
}

export const breakdownBoxPaddingMixin = css`
  padding: ${({ theme }) => theme.spacing(3)};
`;

const StyledPropertyAmountBreakdown = styled(Grid)`
  &.property-amount-breakdown {
    background-color: ${({ theme }) => theme.palette.neutrals.milk};
    ${breakdownBoxPaddingMixin};

    .property-amount-breakdown-item {
      text-align: center;
      padding: ${({ theme }) => theme.spacing(0, 1)};
    }

    .property-amount-breakdown-divider-wrapper {
      padding: ${({ theme }) => theme.spacing(3, 6)};
    }

    .property-amount-breakdown-divider {
      border-color: ${({ theme }) => theme.palette.neutrals.platinum};

      &.vertical {
        margin-right: -1px;
      }
    }

    .property-amount-breakdown-extra-details {
      margin-top: ${({ theme }) => theme.spacing(2)};
      display: flex;
      flex-direction: row;
      justify-content: center;
      gap: ${({ theme }) => theme.spacing(2)};

      & > * {
        text-decoration: underline;
      }
    }
  }
`;

export const PropertyAmountBreakdown = ({ offering }: PropertyAmountBreakdownProps) => {
  const isMobile = useIsMobile();

  return (
    <StyledPropertyAmountBreakdown className="property-amount-breakdown" container columns={12}>
      <Grid className="property-amount-breakdown-item" item xs={12} md={6} display="flex" flexDirection="column">
        <PropertyAmountBreakdownTitle
          formattedMessageProps={{ id: 'property-loan-amount', defaultMessage: 'Property Loan Amount' }}
          tooltipMessageProps={{
            id: 'property-loan-amount-tooltip',
            defaultMessage:
              'The amount of the Total Cost funded by a long-term mortgage loan. Mortgages are placed on the property to provide investors with leverage.',
          }}
        />
        <PropertyAmountBreakdownValue value={offering.debtAmount} />
        <div className="property-amount-breakdown-extra-details">
          <Tooltip
            title={
              offering.hasFlexFinancing ? (
                <FormattedMessage
                  id={'financing.percent.with.flexible.future.financing.description'}
                  defaultMessage={
                    'The percent of the purchase price that is funded by the loan. This property may seek to add up to 70% in leverage through a strategic refinancing.'
                  }
                />
              ) : (
                <FormattedMessage
                  id={'financing-percent-tooltip-text'}
                  defaultMessage={'The percent of the purchase price that is funded by the loan.'}
                />
              )
            }
          >
            <Typography variant="body2" color={({ palette }) => palette.darkened.steel}>
              <FormattedMessage
                id="financing-percent"
                defaultMessage="Financing: {rate}%"
                values={{ rate: offering.debtPercent }}
              />
            </Typography>
          </Tooltip>
          <Tooltip
            title={
              <FormattedMessage
                id="interest-rate-tooltip-text"
                defaultMessage="The interest rate for the Home Loan used to acquire the property. The loan is non-recourse, which means investors are not liable for the debt. The interest expense gets paid out of rental income from the property."
              />
            }
          >
            <Typography variant="body2" color={({ palette }) => palette.darkened.steel}>
              <FormattedMessage
                id="interest-rate"
                defaultMessage="Interest Rate: {rate}%"
                values={{ rate: offering.debtInterestPercent }}
              />
            </Typography>
          </Tooltip>
        </div>
      </Grid>
      {isMobile ? (
        <Grid className="property-amount-breakdown-divider-wrapper" item xs={12}>
          <Divider className="property-amount-breakdown-divider" />
        </Grid>
      ) : (
        <Divider className={clsx('property-amount-breakdown-divider', 'vertical')} flexItem orientation="vertical" />
      )}
      <Grid className="property-amount-breakdown-item" item xs={12} md={6} display="flex" flexDirection="column">
        <PropertyAmountBreakdownTitle
          formattedMessageProps={{ id: 'equity-from-investors', defaultMessage: 'Equity Raised from Investors' }}
          tooltipMessageProps={{
            id: 'equity-from-investors-tooltip',
            defaultMessage:
              'The amount of capital being contributed by investors. It is equal to the total cost minus the loan amount.',
          }}
        />
        <PropertyAmountBreakdownValue
          value={offering.targetRaiseAmount}
          typographyProps={{ color: ({ palette }) => palette.tertiary[500] }}
        />
      </Grid>
    </StyledPropertyAmountBreakdown>
  );
};
